import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DataTableDirective } from 'angular-datatables';
import { catchError, distinctUntilChanged, firstValueFrom, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import { PdfDetailsComponent } from './pdf-details/pdf-details.component';
import { HttpClient } from '@angular/common/http';
import { PermissionsService } from 'src/app/services/permissions.service';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { ScansService } from './scans.service';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from 'src/app/services/dialog.service';
import { AddScansComponent } from './add-scans/add-scans.component';
import Swal from 'sweetalert2';
import { MatCalendar } from '@angular/material/datepicker';
import { DocumentStatusesService } from 'src/app/platform/nomenclatoare/activities-docs-setari/document-statuses/document-statuses.service';
import { DocumentPrioritiesService } from 'src/app/platform/nomenclatoare/activities-docs-setari/document-priorities/document-priorities.service';
import { CopiiService } from '../copii/copii.service';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { UsersService } from 'src/app/services/users.service';

@Component({
    selector: 'app-scans',
    templateUrl: './scans.component.html',
    styleUrls: ['./scans.component.scss']
})
export class ScansComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    @ViewChild(PopoverDirective) popover!: PopoverDirective;
    helper = helper;

    isLoading = false;
    isFilterLoading = false;
    showCard = false;

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    // Popover dates

    @ViewChild('startCalendar') startCalendar!: MatCalendar<Date>;
    @ViewChild('endCalendar') endCalendar!: MatCalendar<Date>;

    today: Date = new Date();
    tomorrow: Date = new Date();
    selectedStartDate: Date = new Date(new Date().getFullYear(), 0, 1);
    private _selectedEndDate: Date = new Date();
    selectedFilteringButton: string = 'current_year';

    documentStatuses!: any;
    documentPriorities!: any;
    entitiesList: any[] = [];
    operatori!: any[];

    setLastXDays(numberOfDays: number): void {
        const date = new Date();
        date.setDate(this.today.getDate() - numberOfDays);
        this.selectedStartDate = date;
        this.updateCalendarView(this.startCalendar, date);
        this.selectedEndDate = this.today;
        this.updateCalendarView(this.endCalendar, this.today);
        this.rerenderDT();
        this.selectedFilteringButton = `last_${numberOfDays}_days`;
    }

    setYear(yearsBack: number): void {
        const year = this.today.getFullYear() - yearsBack;
        this.selectedStartDate = new Date(year, 0, 1); // 1 Ianuarie
        this.updateCalendarView(this.startCalendar, this.selectedStartDate);
        this.selectedEndDate = yearsBack === 0 ? this.today : new Date(year, 11, 31);
        this.updateCalendarView(this.endCalendar, this.selectedEndDate);
        this.rerenderDT();
        this.selectedFilteringButton =
            yearsBack === 0 ? 'current_year' : 'previous_year';
    }

    updateCalendarView(calendar: MatCalendar<Date>, date: Date): void {
        if (calendar) {
            calendar.activeDate = date;
        }
    }

    // Methods used to dinamically select the start date according to the end date

    get selectedEndDate(): Date {
        return this._selectedEndDate;
    }

    set selectedEndDate(date: Date) {
        this._selectedEndDate = date;
        if (this.selectedStartDate > date) {
            this.selectedStartDate = new Date(date);
        }
    }

    resetForm() {
        this.searchParamsForm.reset(this.initialFormValues);
        this.rerenderDT();
        this.showCard = true;
    }

    applyFilters(): void {
        this.rerenderDT(true);
    }

    toggleCard() {
        this.showCard = !this.showCard;
    }

    closePopover(): void {
        if (this.popover) {
            this.popover.hide();
        }
    }

    private destroy$ = new Subject<void>();

    // Filtered fields for search
    filteredEntitati!: any[];
    entitatiControl = new FormControl();

    searchParamsForm!: FormGroup;

    //Form Initial Values

    initialFormValues = {
        denumire: '',
        incarcat_de: -1,
        operator_asignat: -1,
        entity_id: -1,
        status: -1,
        prioritate: -1
    };

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            denumire: new FormControl(''),
            incarcat_de: new FormControl(-1),
            operator_asignat: new FormControl(-1),
            entity_id: new FormControl(-1),
            status: new FormControl(-1),
            prioritate: new FormControl(-1)
        });
    }

    dtLink =
        environment.interop.basePath +
        environment.interop.dgaspc.activitati.scans.getScansDT;

    constructor(
        private http: HttpClient,
        private scansService: ScansService,
        public permissionsService: PermissionsService,
        private dialogService: DialogService,
        private dialog: MatDialog,
        private documentStatusesService: DocumentStatusesService,
        private documentPrioritiesService: DocumentPrioritiesService,
        private copiiService: CopiiService,
        private usersService: UsersService
    ) { }

    ngOnInit(): void {
        this.renderDT();
        this.buildSearchParamsForm();
        this.loadDocumentStatuses();
        this.loadDocumentPriorities();
        this.loadEntities();
        this.renderFieldsSearch();
        this.loadOperatori();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();

        this.destroy$.next();
        this.destroy$.complete();
    }

    formatDate(date: Date) {
        let d = new Date(date),
            month = '' + (d.getMonth() + 1),
            day = '' + d.getDate(),
            year = d.getFullYear();

        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;

        return [year, month, day].join('-');
    }

    renderDT() {
        this.isLoading = true;
        this.dtOptions = {
            serverSide: true,
            searching: false,
            responsive: true,
            pageLength: 25,

            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;
                dataTablesParameters.filters = this.searchParamsForm.value;
                dataTablesParameters.date_from = this.formatDate(this.selectedStartDate);
                dataTablesParameters.date_to = this.formatDate(this.selectedEndDate);

                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;
                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id',
                },
                {
                    title: 'Denumire',
                    data: 'entity',
                    orderable: false,
                    render: (data: any, type: any, row: any) => {
                        const userUrl = '/assets/icons/icon-user.svg';
                        const hasEntitate = row.entity_id !== null ? true : false;

                        if (hasEntitate) {
                            return `
                                    <span>${row.display_name}</span><br/><br/>

                                    <span style="color:#bdbdbd; font-size:12px"> Entitate</span><br/>

                                    <div class="d-flex flex-row align-items-center gap-2">
                                        <img src="${userUrl}" alt="Image" style="width: 20px; height: 20px;">
                                        <span>${data.full_name}</span>
                                    </div>
                                        
                                `
                        } else {
                            return `
                                    <span>${row.display_name}</span>
                                `;
                        }
                    }
                },
                {
                    title: 'Încărcat de',
                    data: 'uploaded_by_user_name',
                    orderable: false,
                },
                {
                    title: 'Încărcat la',
                    data: 'created_at',
                    orderable: false,
                    render: (data: any) => {
                        return new Date(data).toLocaleDateString('ro-RO', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                        });
                    }
                },
                {
                    title: 'Operator asignat',
                    data: 'assigned_to_user_name',
                    render: (data: any, type: any, row: any) => {
                        return `
                                <span>${row.assigned_to_user_id !== null ? data : 'N/A'}</span>
                            `
                    }
                },
                {
                    title: 'Prioritate',
                    data: 'priority_name',
                    orderable: false,
                    render: (data: any, type: any, row: any) => {
                        if (data !== null) {
                            return `<span style="color: ${row.priority_color}">${data}</span>`
                        } else return 'N/A'
                    }
                },
                {
                    title: 'Ultima actualizare',
                    data: 'updated_at',
                    orderable: false,
                    render: (data: any, type: any, row: any) => {
                        const formattedDate = new Date(data).toLocaleDateString('ro-RO', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                        });
                        return `
                            <span>${formattedDate}</span>
                            <br/><br/>
                            <span style="color:#bdbdbd; font-size:12px">Status</span><br/>
                            <span style="color: ${row.status_color}">${row.status_name}</span>
                        `
                    }
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                $('td', row).on('click', () => {
                    this.openPdfViewer(data);
                });
                return row;
            },
        };
    }

    openAddDialog() {
        const dialogRef = this.dialog.open(AddScansComponent, {
            ...this.dialogService.getDialogConfig(),
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Document adăugat cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    async openPdfViewer(rowData: any) {
        try {
            // Afișează loading
            this.isLoading = true;

            // Descarcă PDF-ul folosind serviciul cu ID-ul din rowData
            const pdfBlob = await firstValueFrom(
                this.scansService.downloadScan(rowData.id)
            );

            // Creează URL-ul pentru PDF
            const pdfUrl = URL.createObjectURL(pdfBlob);

            // Deschide dialog-ul cu PDF-ul descărcat
            const dialogRef = this.dialog.open(PdfDetailsComponent, {
                minHeight: '100vh',
                maxHeight: '100vh',
                minWidth: '100vw',
                maxWidth: '100vw',
                disableClose: true,
                autoFocus: false,
                restoreFocus: false,
                data: {
                    pdf: {
                        id: rowData.id,
                        name: rowData.display_name || `Document_${rowData.id}.pdf`,
                        url: pdfUrl,
                        blob: pdfBlob,
                        size: pdfBlob.size,
                        uploadDate: new Date() // sau rowData.created_at dacă există
                    },
                    rowData: rowData,
                    rowId: rowData.id,
                    pdfIndex: 0, // Nu mai e relevant pentru că nu mai avem array de PDFs
                    entitiesList: this.entitiesList,
                    documentStatuses: this.documentStatuses,
                    documentPriorities: this.documentPriorities,
                    operatori: this.operatori,
                },
            });

            dialogRef.afterClosed().subscribe(() => {
                // Cleanup - revoke URL-ul pentru a elibera memoria
                URL.revokeObjectURL(pdfUrl);
                this.rerenderDT();
            });

        } catch (error) {
            console.error('Eroare la descărcarea PDF-ului:', error);

            // Afișează mesaj de eroare
            Swal.fire({
                icon: 'error',
                title: 'Eroare',
                text: 'Nu s-a putut încărca PDF-ul. Vă rugăm să încercați din nou.',
                confirmButtonText: 'OK'
            });
        } finally {
            this.isLoading = false;
        }
    }

    rerenderDT(paginate = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(paginate);
        });

        this.showCard = false;
    }

    loadDocumentStatuses() {
        this.isFilterLoading = true;
        this.documentStatusesService.getListActive().subscribe({
            next: (response: any) => {
                this.documentStatuses = response;
                this.isFilterLoading = false;
            },
            error: (error: any) => {
                console.error('Error loading document statuses:', error);
                this.isFilterLoading = false;
            }
        })
    }

    loadDocumentPriorities() {
        this.isFilterLoading = true;
        this.documentPrioritiesService.getListActive().subscribe({
            next: (response: any) => {
                this.documentPriorities = response;
                this.isFilterLoading = false;
            },
            error: (error: any) => {
                console.error('Error loading document priorities:', error);
                this.isFilterLoading = false;
            }
        })
    }

    loadEntities(): void {
        this.isFilterLoading = true;
        this.copiiService.getCopii().subscribe({
            next: (response) => {
                this.entitiesList = response;
                this.filteredEntitati = this.entitiesList;
                this.isFilterLoading = false;
            },
            error: (error) => {
                console.error('Error loading entities:', error);
                this.isFilterLoading = false;
                Swal.fire({
                    icon: 'error',
                    title: 'Eroare',
                    text: 'Nu s-au putut încărca entitățile. Vă rugăm să încercați din nou.',
                    confirmButtonText: 'OK'
                });
            }
        });
    }

    renderFieldsSearch() {
        this.entitatiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredEntitati = this.entitiesList;
                    return of(this.entitiesList);
                }
                return this.filterEntitati(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredEntitati = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterEntitati(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.entitiesList.filter(entitate =>
            this.normalizeString(entitate.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onEntitatiSelectClosed() {
        const selectedValue = this.entitatiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredEntitati.some(entitate =>
                entitate.id === this.searchParamsForm.get('entity_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.entitatiControl.setValue(null);
                this.filteredEntitati = this.entitiesList;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadOperatori() {
        this.isFilterLoading = true;
        this.usersService.getUsers().subscribe({
            next: (response: any) => {
                this.operatori = response;
                this.isFilterLoading = false;
            },
            error: (error: any) => {
                console.error('Error loading operators:', error);
                this.isFilterLoading = false;
            }
        })
    }
}
